import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, CheckCircle2, XCircle, Award, Calendar, User, BookOpen, Shield, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyCertificationPage = () => {
  const [searchParams] = useSearchParams();
  const [certId, setCertId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Auto-search from QR code URL parameter
  useEffect(() => {
    const cid = searchParams.get('cid');
    if (cid) {
      setCertId(cid);
      handleVerify(cid);
    }
  }, [searchParams]);

  const handleVerify = async (id) => {
    const searchId = (id || certId).trim();
    if (!searchId) return;
    setLoading(true);
    setSearched(true);
    setResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/verify-certificate`, {
        certificate_id: searchId
      });
      setResult(response.data);
    } catch {
      setResult({ success: false, error: true });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  const shareOnLinkedIn = () => {
    if (!result?.data) return;
    const text = `I'm proud to share that I hold the ${result.data.certification_title} certification from CloudXAI Technologies! Certificate ID: ${result.data.certificate_id}. Verify at: ${window.location.origin}/verify?cid=${result.data.certificate_id}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin + '/verify?cid=' + result.data.certificate_id)}&summary=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0B0F1A 0%, #020617 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-1/4 w-80 h-80 bg-[#00E5FF] rounded-full blur-[150px]"></div>
        </div>
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] mb-6 shadow-xl shadow-[#00E5FF]/50">
            <Shield className="w-10 h-10 text-[#0B0F1A]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 font-['Space_Grotesk']" data-testid="verify-heading">
            Verify CloudXAI <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Certification</span>
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto">
            Enter the Certificate ID to verify the authenticity of a CloudXAI credential.
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-[#111827] border-2 border-[#00E5FF]/30 shadow-2xl shadow-[#00E5FF]/10">
            <CardContent className="p-8">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    value={certId}
                    onChange={(e) => setCertId(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter Certificate ID (e.g., CXAI-AI-2026-0045)"
                    className="pl-12 py-6 text-base bg-[#0B0F1A] border-[#00E5FF]/30 text-white placeholder:text-[#9CA3AF] focus:border-[#00E5FF] focus:ring-[#00E5FF]"
                    data-testid="verify-input"
                  />
                </div>
                <Button
                  onClick={() => handleVerify()}
                  disabled={loading || !certId.trim()}
                  className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-base rounded-lg shadow-lg shadow-[#00E5FF]/40 disabled:opacity-50"
                  data-testid="verify-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
                </Button>
              </div>
              <p className="text-[#9CA3AF] text-xs mt-3 text-center">
                Format: CXAI-[PROGRAM]-[YEAR]-[NUMBER] — e.g., CXAI-AI-2026-0045
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Result Section */}
      {searched && !loading && (
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            {result?.found ? (
              <Card className="bg-[#111827] border-2 border-[#1DE9B6]/40 shadow-2xl shadow-[#1DE9B6]/10 overflow-hidden" data-testid="verify-result-found">
                {/* Verified Banner */}
                <div className="bg-gradient-to-r from-[#1DE9B6] to-[#00E5FF] px-6 py-4 flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-[#0B0F1A]" />
                  <div>
                    <h3 className="text-[#0B0F1A] font-bold text-lg font-['Space_Grotesk']">Verified CloudXAI Credential</h3>
                    <p className="text-[#0B0F1A]/70 text-sm">This certificate has been authenticated.</p>
                  </div>
                </div>

                <CardContent className="p-8">
                  <div className="space-y-6">
                    {/* Seal */}
                    <div className="flex justify-center mb-2">
                      <div className="w-28 h-28 rounded-full border-4 border-[#00E5FF]/40 flex items-center justify-center relative">
                        <div className="w-20 h-20 rounded-full border-2 border-[#00E5FF]/30 flex flex-col items-center justify-center bg-[#00E5FF]/5">
                          <Award className="w-6 h-6 text-[#00E5FF] mb-0.5" />
                          <span className="text-[#00E5FF] font-bold text-[8px] uppercase tracking-wider">Certified</span>
                          <span className="text-[#00E5FF] font-bold text-[8px] uppercase tracking-wider">Professional</span>
                        </div>
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0B0F1A]">
                        <User className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[#9CA3AF] text-xs">Candidate Name</p>
                          <p className="text-white font-medium" data-testid="verify-candidate-name">{result.data.candidate_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0B0F1A]">
                        <Award className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[#9CA3AF] text-xs">Certification</p>
                          <p className="text-white font-medium" data-testid="verify-cert-title">{result.data.certification_title}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0B0F1A]">
                        <BookOpen className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[#9CA3AF] text-xs">Level</p>
                          <p className="text-white font-medium">{result.data.level}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0B0F1A]">
                        <Calendar className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[#9CA3AF] text-xs">Issue Date</p>
                          <p className="text-white font-medium">{result.data.issue_date}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0F1A]">
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[#00E5FF]" />
                        <span className="text-[#9CA3AF] text-sm">Certificate ID</span>
                      </div>
                      <code className="text-[#1DE9B6] font-mono font-bold" data-testid="verify-cert-id">{result.data.certificate_id}</code>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0F1A]">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#00E5FF]" />
                        <span className="text-[#9CA3AF] text-sm">Status</span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        result.data.status === 'Active'
                          ? 'bg-[#1DE9B6]/20 text-[#1DE9B6]'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`} data-testid="verify-status">
                        {result.data.status}
                      </span>
                    </div>

                    {/* Share on LinkedIn */}
                    <Button
                      onClick={shareOnLinkedIn}
                      className="w-full bg-[#0077B5] hover:bg-[#005F8F] text-white font-semibold py-5 text-base rounded-lg transition-all"
                      data-testid="share-linkedin-btn"
                    >
                      Share on LinkedIn
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#111827] border-2 border-red-500/30" data-testid="verify-result-not-found">
                <CardContent className="p-8 text-center">
                  <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <h3 className="text-white font-bold text-xl mb-2 font-['Space_Grotesk']">No Record Found</h3>
                  <p className="text-[#9CA3AF]">
                    No certificate found with ID <code className="text-red-400 font-mono">{certId.toUpperCase()}</code>. Please double-check the ID and try again.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Trust Section */}
      {!searched && (
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#9CA3AF] text-sm mb-8">Trusted verification — the same model used by</p>
            <div className="flex flex-wrap justify-center gap-8 text-[#9CA3AF]/50 text-lg font-bold tracking-wider">
              <span>Microsoft</span>
              <span>AWS</span>
              <span>Google</span>
              <span>Oracle</span>
              <span>Cisco</span>
            </div>
          </div>
        </section>
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default VerifyCertificationPage;
